import React, { useState } from "react";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { API } from "@/lib/api";
import {
  X, Archive, House, EyeSlash, ArrowSquareOut, Spinner,
} from "@phosphor-icons/react";

/**
 * PortalPackModal — iter v28 r348
 *
 * Modal "Cosa pubblicare" che si apre prima del download del pacchetto
 * Idealista/Immobiliare. Permette di:
 *  - Vedere quante foto sono state escluse manualmente dai portali
 *  - Scegliere quale versione della pianta includere:
 *    - Arredata (default · best per portali)
 *    - Originale (planimetria nuda)
 *    - Entrambe
 *    - Nessuna pianta
 *  - Sapere quante foto verranno effettivamente caricate (max 40 per Idealista)
 */
export default function PortalPackModal({
  propertyId,
  portal = "idealista",
  includedCount,
  excludedCount,
  hasFurnishedPlan,
  hasOriginalPlan,
  onClose,
  onGoToPhotos,
}) {
  // Default pianta: arredata se esiste, altrimenti originale, altrimenti nessuna
  const defaultPlan = hasFurnishedPlan ? "furnished" : hasOriginalPlan ? "original" : "none";
  const [planChoice, setPlanChoice] = useState(defaultPlan);
  const [downloading, setDownloading] = useState(false);

  const portalLabel = portal === "idealista" ? "Idealista" : portal === "immobiliare" ? "Immobiliare.it" : "portale";
  const maxPhotos = portal === "idealista" ? 40 : 60;
  // Effettive da pubblicare = min(includedCount, maxPhotos)
  const effectiveCount = Math.min(includedCount, maxPhotos);
  const overflow = Math.max(0, includedCount - maxPhotos);

  const planOptions = [
    {
      v: "furnished",
      l: "Arredata",
      sub: "Pianta arredata (consigliata per i portali)",
      disabled: !hasFurnishedPlan,
      disabledHint: "Genera prima la pianta arredata",
    },
    {
      v: "original",
      l: "Originale",
      sub: "Planimetria nuda, senza arredo",
      disabled: !hasOriginalPlan,
      disabledHint: "Carica prima la planimetria",
    },
    {
      v: "both",
      l: "Entrambe",
      sub: "Sia arredata sia originale",
      disabled: !(hasFurnishedPlan && hasOriginalPlan),
      disabledHint: "Servono entrambe le versioni",
    },
    {
      v: "none",
      l: "Nessuna",
      sub: "Non includere la pianta",
      disabled: false,
    },
  ];

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = Cookies.get("vista_jwt");
      toast.info(`Preparo il pacchetto per ${portalLabel}…`);
      const params = new URLSearchParams({
        kind: "enhanced",
        max: String(maxPhotos),
        optimize: "portal",
        floorplan: planChoice,
      });
      const r = await fetch(
        `${API}/properties/${propertyId}/photos/zip?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pronto-${portal}-${propertyId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Pacchetto ${portalLabel} pronto (${effectiveCount} foto)`);
      onClose?.();
    } catch (e) {
      toast.error("Download fallito: " + (e.message || ""));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="portal-pack-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#EAE7DF] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">
              Pacchetto {portalLabel}
            </div>
            <h3 className="font-editorial text-xl mt-1 text-[#1C1C1A]">Cosa pubblicare</h3>
            <p className="text-[12px] text-[#6B6A65] mt-1 leading-snug">
              Pronto prepara il file ZIP da trascinare su {portalLabel}.
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center bg-[#F1F4ED] hover:bg-[#E5E7E0] text-[#1C1C1A] transition-colors"
            aria-label="Chiudi"
            data-testid="portal-pack-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* SEZIONE FOTO */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">
              Foto
            </div>
            <div className="bg-[#F5F4F0] border border-[#EAE7DF] rounded-xl p-4">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-[#1C1C1A]" data-testid="portal-pack-included-count">
                  {effectiveCount}
                </span>
                <span className="text-[13px] text-[#6B6A65]">
                  {effectiveCount === 1 ? "foto da caricare" : "foto da caricare"}
                </span>
              </div>
              {excludedCount > 0 && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-[#9A968B]">
                  <EyeSlash size={11} weight="fill" className="text-[#D14D4D]" />
                  {excludedCount} {excludedCount === 1 ? "foto esclusa" : "foto escluse"} dai portali
                </div>
              )}
              {overflow > 0 && (
                <div className="mt-2 text-[11.5px] text-[#C49A2A] leading-snug">
                  ⚠ Hai {includedCount} foto incluse → {portalLabel} accetta max {maxPhotos}.
                  Le ultime {overflow} non verranno caricate (riordina nel tab Foto per scegliere le prime).
                </div>
              )}
              <button
                onClick={onGoToPhotos}
                className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] text-[#7C8B6E] hover:text-[#1C1C1A] font-semibold underline underline-offset-2"
                data-testid="portal-pack-edit-photos"
              >
                Modifica selezione <ArrowSquareOut size={11} weight="bold" />
              </button>
            </div>
          </div>

          {/* SEZIONE PIANTA */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2 inline-flex items-center gap-1.5">
              <House size={11} weight="fill" /> Pianta
            </div>
            <div className="space-y-2">
              {planOptions.map((opt) => {
                const active = planChoice === opt.v;
                return (
                  <button
                    key={opt.v}
                    onClick={() => !opt.disabled && setPlanChoice(opt.v)}
                    disabled={opt.disabled}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-start gap-3 ${
                      opt.disabled
                        ? "bg-[#FAFAF7] border-[#EAE7DF] text-[#9A968B] cursor-not-allowed"
                        : active
                          ? "bg-[#F1F4ED] border-[#7C8B6E] text-[#1C1C1A]"
                          : "bg-white border-[#EAE7DF] hover:border-[#7C8B6E] text-[#1C1C1A]"
                    }`}
                    data-testid={`portal-pack-plan-${opt.v}`}
                    aria-pressed={active}
                  >
                    <span
                      className={`mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 inline-flex items-center justify-center transition-colors ${
                        active && !opt.disabled
                          ? "border-[#7C8B6E] bg-[#7C8B6E]"
                          : "border-[#C9C6BF] bg-white"
                      }`}
                    >
                      {active && !opt.disabled && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-semibold">{opt.l}</span>
                      <span className="block text-[11.5px] text-[#6B6A65] mt-0.5 leading-snug">
                        {opt.disabled ? opt.disabledHint : opt.sub}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-5 py-4 border-t border-[#EAE7DF] bg-[#FAFAF7]">
          <button
            onClick={handleDownload}
            disabled={downloading || effectiveCount === 0}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1C1C1A] hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed text-white py-3 rounded-full text-[13.5px] font-semibold transition-colors"
            data-testid="portal-pack-download"
          >
            {downloading ? (
              <><Spinner size={14} className="animate-spin" /> Preparo lo ZIP…</>
            ) : (
              <><Archive size={14} weight="bold" /> Scarica pacchetto · {effectiveCount} foto</>
            )}
          </button>
          {effectiveCount === 0 && (
            <p className="text-[11.5px] text-[#D14D4D] mt-2 text-center">
              Tutte le foto sono escluse. Includi almeno una foto per scaricare il pacchetto.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
